import { z } from "zod";
import { publicReportDateSchema, publicThumbnailSchema } from "./reporting.js";
/**
 * The monthly creative strategist report: every Meta ad that delivered in a
 * month, credited to its creative strategist and, separately, its designer.
 * It is published once a month to BigQuery by the video-reports tooling and is
 * readable by every signed-in user, independent of brand grants.
 */
export const MAXIMUM_STRATEGIST_REPORT_ADS = 20_000;
export const MAXIMUM_STRATEGIST_REPORT_CREDITS = 60_000;
export const MAXIMUM_STRATEGIST_REPORT_MONTHS = 120;
/** A report month in URLs and API paths: `YYYY-MM`. */
export const strategistReportMonthSchema = z
    .string()
    .regex(/^(?!0000-)\d{4}-(?:0[1-9]|1[0-2])$/u);
/** Meta ad IDs are numeric strings. */
export const strategistReportAdIdSchema = z.string().regex(/^[0-9]{1,32}$/u);
export const strategistReportBoardSchema = z.enum(["strategist", "designer"]);
/** `left`: people who left the company; `none`: no owner or no designer code. */
export const strategistReportPersonKindSchema = z.enum([
    "person",
    "left",
    "none",
]);
const amount = z.number().finite();
export const strategistReportMonthSummarySchema = z
    .object({
    dataThrough: publicReportDateSchema,
    month: strategistReportMonthSchema,
    publishedAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const strategistReportMonthsResponseSchema = z
    .object({
    months: z
        .array(strategistReportMonthSummarySchema)
        .max(MAXIMUM_STRATEGIST_REPORT_MONTHS),
})
    .strict();
/** One ad with its whole-month numbers; credits carry each person's share. */
export const strategistReportAdSchema = z
    .object({
    accountName: z.string().min(1).max(512),
    adId: strategistReportAdIdSchema,
    adName: z.string().max(2_048).nullable(),
    adsetName: z.string().max(2_048).nullable(),
    brand: z.string().min(1).max(128),
    campaignName: z.string().max(2_048).nullable(),
    currency: z.string().regex(/^[A-Z]{3}$/u),
    firstSpend: publicReportDateSchema.nullable(),
    hasVideo: z.boolean(),
    /** Meta impressions and outbound clicks in the month; null in months published before they were added. */
    impressions: amount.nullable(),
    isNew: z.boolean(),
    isRetargeting: z.boolean(),
    metaRevenue: amount,
    orders: amount,
    outboundClicks: amount.nullable(),
    revenue: amount,
    revenueSource: z.enum(["Shopify", "Meta"]),
    spend: amount,
    thumbnailUrl: publicThumbnailSchema,
    urlHasAdId: z.boolean(),
})
    .strict();
export const strategistReportCreditSchema = z
    .object({
    adId: strategistReportAdIdSchema,
    board: strategistReportBoardSchema,
    hidden: z.boolean(),
    kind: strategistReportPersonKindSchema,
    person: z.string().min(1).max(256),
    share: z.number().finite().min(0).max(1),
})
    .strict();
export const strategistReportResponseSchema = z
    .object({
    ads: z.array(strategistReportAdSchema).max(MAXIMUM_STRATEGIST_REPORT_ADS),
    credits: z
        .array(strategistReportCreditSchema)
        .max(MAXIMUM_STRATEGIST_REPORT_CREDITS),
    dataThrough: publicReportDateSchema,
    metaFallback: z.boolean(),
    month: strategistReportMonthSchema,
    publishedAt: z.iso.datetime({ offset: true }),
})
    .strict();
/**
 * What the ad looks like on Facebook: page, primary text, media, link domain,
 * headline and call to action. Loaded per ad so the month stays small.
 */
export const strategistReportAdPreviewSchema = z
    .object({
    adId: strategistReportAdIdSchema,
    adName: z.string().max(2_048).nullable(),
    body: z.string().max(20_000).nullable(),
    /** Call to action as Meta names it, e.g. SHOP_NOW. */
    callToAction: z.string().regex(/^[A-Z0-9_]{1,64}$/u).nullable(),
    /** Only the destination's host: the full link carries tracking parameters. */
    displayLink: z.string().max(253).nullable(),
    hasVideo: z.boolean(),
    headline: z.string().max(2_048).nullable(),
    imageUrl: publicThumbnailSchema,
    pageName: z.string().max(512).nullable(),
    thumbnailUrl: publicThumbnailSchema,
})
    .strict();
/**
 * The video editor report for the same month: each video ad's creative (copies, re-uploads and
 * placement files of one ad name share a creative key), its ticket, its video metrics and the
 * editors credited for it. Spend and revenue come from the month's strategist report.
 */
export const strategistEditorAdSchema = z
    .object({
    adId: strategistReportAdIdSchema,
    creativeFirstSpend: publicReportDateSchema.nullable(),
    creativeKey: z.string().min(1).max(4_096),
    creativeName: z.string().min(1).max(2_048),
    impressions: amount,
    launched: z.boolean(),
    matchMethod: z.string().max(256).nullable(),
    thruplays: amount,
    ticketName: z.string().max(512).nullable(),
    ticketNo: z.number().int().nullable(),
    video3sViews: amount,
    videoImpressions: amount,
})
    .strict();
export const strategistEditorCreditSchema = z
    .object({
    adId: strategistReportAdIdSchema,
    editor: z.string().min(1).max(256),
    kind: z.enum(["person", "none"]),
    share: z.number().finite().min(0).max(1),
})
    .strict();
export const strategistEditorReportResponseSchema = z
    .object({
    ads: z.array(strategistEditorAdSchema).max(MAXIMUM_STRATEGIST_REPORT_ADS),
    credits: z
        .array(strategistEditorCreditSchema)
        .max(MAXIMUM_STRATEGIST_REPORT_CREDITS),
    month: strategistReportMonthSchema,
})
    .strict();
//# sourceMappingURL=strategist-report.js.map